import { Create, Header, useForm, useSelect } from "@refinedev/antd";
import { ErrorComponent, useApiUrl, useCreate, useCreateMany, useCustom, useGetIdentity, useGo, useMany, useNotification, useOne, useParsed, useTranslate } from "@refinedev/core";
import { Alert, Button, Card, Col, Divider, Flex, Form, Input, InputNumber, Result, Row, Select, Space, Statistic, Switch, theme, Typography, Upload, UploadProps } from "antd";
import { useParams } from "react-router-dom";
import FormRender from "../forms/FormRender";
import { useEffect, useState } from "react";
import { CodeOutlined, CodeTwoTone, DollarOutlined, InboxOutlined, PaperClipOutlined, SendOutlined, UploadOutlined, IdcardOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { FormContainerTitle } from "../forms/components/Container";
import { NotFound } from "../../components/errors";
import { CustomerBackButton } from "./components/customerBackButton";
import { CustomerBreadcrumb } from "./components/customerBreadcrumb";
import { PageLoading } from "../../components/pageLoading";
import { useCredit, useDeductLocalAccountCredit } from "../../hooks/account";
import { CreditBuyModal } from "../../components/credit-buy-modal/index";
import Dragger from "antd/es/upload/Dragger";
import { useAdmin } from "../../hooks/auth";
import { BackButton } from "../../components/backButton";
import { ORDER_CREATE_STEPPED_KEY } from "../../authProvider";
import { OrderCreateStepped } from "./components/OrderCreateStepped";
import { OpeningHoursNotice } from "../../components/tenant/OpeningHoursNotice";
import { getDevOrderFormValues } from "./utils/devOrderFormAutofill";

export const CustomerOrderCreate = () => {
    const _t = useTranslate();
    const { formProps, saveButtonProps, queryResult } = useForm();
    const [formSchema, setFormSchema] = useState([]);
    const identity = useGetIdentity();
    const [user, setUser] = useState({});
    const [success, setSuccess] = useState(false);
    const [createdOrderId, setCreatedOrderId] = useState<number | undefined>(undefined);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const { form, onFinish } = useForm();
    const { mutate } = useCreate();
    const { mutate: mutateMany } = useCreateMany();
    const apiUrl = useApiUrl();
    const go = useGo();
    const { open } = useNotification();
    const { id } = useParsed();
    const params = useParams();
    const isAdmin = useAdmin();
    const [products, setProducts] = useState([]);
    const [productsAmounts, setProductsAmounts] = useState([]);
    const { Title } = Typography;
    const userAccountCredit = useCredit();
    const deductLocalCredit = useDeductLocalAccountCredit();
    const [accountCredit, setAccountCredit] = useState(0);
    const [isPrepaid, setIsPrepaid] = useState(false);
    const [totalAmount, setTotalAmount] = useState(0);
    const [productOptions, setProductOptions] = useState([]);
    const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
    const [comments, setComments] = useState("");
    const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(undefined);
    const [selectedAccount, setSelectedAccount] = useState<any>(null);
    const [steppedMode, setSteppedMode] = useState<boolean>(true);
    const [isSendingOrder, setIsSendingOrder] = useState(false);
    const { token } = theme.useToken();

    const { selectProps: productList } = useSelect({
        resource: "products",
        optionLabel: "id",
        optionValue: "price",
    });

    const { selectProps: productSelectProps } = useSelect({
        resource: "products",
        optionValue: "id",
        searchField: "name",
        sorters: [
            {
                field: "name",
                order: "asc",
            },
        ],
    });

    const productQueryResult = useCustom({
        url: `${apiUrl}/products/prices`,
        method: "get",
        config: {
            query: selectedAccountId ? { accountId: selectedAccountId } : {},
        },
        queryOptions: {
            enabled: !!selectedAccountId || !isAdmin,
        },
    });

    const { selectProps: accountSelectProps } = useSelect({
        resource: "accounts",
        optionLabel: "name",
        optionValue: "id",
        searchField: "name",
        sorters: [
            {
                field: "name",
                order: "asc",
            },
        ],
    });


    useEffect(() => {
        if (productQueryResult.data && productOptions.length === 0) {
            let groupedOptions: { [key: string]: { label: string; options: any[] } } = productQueryResult.data?.data.reduce((groups: { [key: string]: { label: string; options: any[] } }, item: any) => {
                let group = `${item.group}`; // replace with your actual group property
                if (group == "null") {
                    group = ""
                }
                if (!groups[group]) {
                    groups[group] = { label: group, options: [] };
                }
                groups[group].options.push({
                    label: isPrepaid
                        ? `${item.name} - ${item.price} ${_t("credits.credits")}`
                        : `${item.name} | ${item.type}`,
                    value: item.id,
                });
                return groups;
            }, {});
            //console.log("Grouped options", JSON.stringify(groupedOptions));
            const sortedGroupedOptions = Object.entries(groupedOptions)
                .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                .reduce((sortedObj: { [key: string]: { label: string; options: any[] } }, [key, value]) => {
                    sortedObj[key] = value;
                    return sortedObj;
                }, {});
            //console.log("sortedGroupedOptions ", JSON.stringify(sortedGroupedOptions));
            // Use sortedGroupedOptions instead of groupedOptions
            console.log("Product options", Object.values(sortedGroupedOptions));
            setProductOptions(Object.values(sortedGroupedOptions));
        }
    }, [productQueryResult, isPrepaid]);


    const accountIdFromParams = params?.id ? parseInt(params.id) : undefined;

    useEffect(() => {
        if (identity.data) {
            setUser(identity.data);
            console.log("User info", identity.data);
            console.log("Account data", identity.data.account);

            // Set account from params if available, otherwise use user's account
            if (accountIdFromParams) {
                setSelectedAccountId(accountIdFromParams);
            } else if (identity.data.account) {
                setSelectedAccountId(identity.data.account.id);
                setSelectedAccount(identity.data.account);
            }
        }
    }, [identity, accountIdFromParams]);

    // Fetch account details when accountId changes
    // For admin users, always fetch if accountId is set (could be from params or dropdown)
    // For regular users, only fetch if accountId differs from their own account
    const accountQuery = useOne({
        resource: "accounts",
        id: selectedAccountId,
        queryOptions: {
            enabled: !!selectedAccountId && (isAdmin || selectedAccountId !== user?.account?.id),
        },
    });

    useEffect(() => {
        if (accountQuery.data?.data) {
            setSelectedAccount(accountQuery.data.data);
            setIsPrepaid(accountQuery.data.data?.billingMode === "prepaid");
            setAccountCredit(accountQuery.data.data?.credit ?? 0);
        } else if (user?.account) {
            setSelectedAccount(user.account);
            setIsPrepaid(user?.account?.billingMode === "prepaid");
            setAccountCredit(userAccountCredit);
        }
    }, [accountQuery.data, user, userAccountCredit]);

    const formQuery = useOne({
        resource: "forms",
        id: "default",
    });

    useEffect(() => {
        if (formQuery.data?.data && formSchema.length === 0) {
            const formData = formQuery.data?.data;
            const sc = JSON.parse(formData?.schema);
            console.log("Form schema", sc);
            setFormSchema(sc);
        }
    }, [formQuery]);

    useEffect(() => {
        if (import.meta.env.PROD || formSchema.length === 0 || steppedMode) return;
        form?.setFieldsValue(getDevOrderFormValues(formSchema));
    }, [formSchema, steppedMode, form]);

    useEffect(() => {
        productList && console.log("Product list", productList);
    }, [productList]);


    if (formQuery.isLoading /*|| accountQuery.isLoading*/) {
        return <PageLoading />;
    }

    if (formQuery.isError /*|| accountQuery.isError*/) {
        return <NotFound />;
    }

    if (formQuery.isSuccess) {
        console.log("Form data", formQuery.data);
        if (!formQuery.data?.data) {
            return <NotFound />;
        }
    }

    const formId = formQuery.data?.data?.id != null ? String(formQuery.data.data.id) : undefined;
    if (!formId) {
        return <NotFound />;
    }

    const onSubmit = async (vs) => {
        const values = form?.getFieldsValue();
        console.log("Form values", values);

        // Determine which account to use
        const accountIdToUse = selectedAccountId || user?.account?.id;

        if (!accountIdToUse) {
            open?.({
                message: _t("orders.fields.account") + " " + _t("buttons.notAccessTitle"),
                type: "error",
                key: "account-required",
            });
            return;
        }

        setIsSendingOrder(true);

        const mapped = formSchema.map((item: any) => {
            if (item.children?.length > 0) {
                return {
                    ...item,
                    children: item.children.map((child: any) => {
                        return {
                            ...child,
                            value: values[child.name],
                        };
                    }),
                };
            }
            return {
                ...item,
                value: values[item.name],
            };
        });

        const order = {
            productIds: products.map((v: number) => v.toString()),
            accountId: `${accountIdToUse}`,
            createdBy: `${user.id}`,
            payload: JSON.stringify(mapped),
            status: "submitted",
            comments: comments,
        };

        console.log("Order", order);

        mutate({
            resource: "orders",
            values: order,
        }, {
            onSuccess: (data) => {
                console.log("Order created successfully", data);
                if (isPrepaid) {
                    const newCredit = deductLocalCredit(totalAmount, accountIdToUse);
                    if (newCredit !== null) {
                        setAccountCredit(newCredit);
                    }
                }
                const order = data.data;
                if (order?.id != null) {
                    setCreatedOrderId(Number(order.id));
                }
                const attachmentList: any[] = uploadedFiles.map((file) => {
                    return {
                        name: file.name,
                        fileId: file.fileId,
                        metadata: file.metadata,
                        orderId: `${order.id}`,
                        direction: 'in'
                    };
                });

                if (attachmentList.length > 0) {

                    mutateMany({
                        resource: 'attachments',
                        values: attachmentList,
                    }, {
                        onSuccess: () => {
                            console.log("Attachment created successfully");
                            setSuccess(true);
                        },
                        onError: () => {
                            console.log("Attachment creation failed");
                            setIsSendingOrder(false);
                            open?.({
                                message: _t("orders.errors.createAttachment"),
                                type: "error",
                                key: "unique-id",
                                duration: 0,
                            });
                        }
                    });
                } else {
                    setSuccess(true);
                }
            },
            onError: () => setIsSendingOrder(false),
        });

        console.log("Mapped", mapped);

    }

    const fileUploadProps: UploadProps = {
        name: 'file',
        action: apiUrl + '/files/upload',
        method: 'POST',
        /*headers: {
            authorization: 'Bearer ' + authToken,
        },*/
        onChange(info) {
            if (info.file.status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (info.file.status === 'done') {
                //message.success(`${info.file.name} file uploaded successfully`);
                console.log("File uploaded successfully");
                console.log(info.file);
                const res = info.file;
                if (res) {
                    console.log("File uploaded successfully", res.response.file);
                    //res.file.path
                    const newFile = {
                        name: res.name,
                        metadata: JSON.stringify({
                            type: res.type,
                            size: res.size
                        }),
                        fileId: res.response.file.id,
                    }
                    setUploadedFiles([...uploadedFiles, newFile]);
                    /*const attachment = {
                        name: res.name,
                        fileId: res.response.file.id,
                        metadata: JSON.stringify(metadata),
                        orderId: `${record.id}`,
                    };
                    mutate({
                        resource: 'attachments',
                        values: attachment,
                        onSuccess: () => {
                            console.log("Attachment created successfully");
                        }
                    });*/
                }
            } else if (info.file.status === 'error') {
                //message.error(`${info.file.name} file upload failed.`);
                if (info.file.error.status == 401) {
                    // Unauthorized, try refreshing token

                }
                console.log("File upload failed",);
            }
        },
    };

    const handleBuyModalCancel = () => {
        setIsBuyModalOpen(false);
    };

    const handleBuyModalOpen = () => {
        setIsBuyModalOpen(true);
    };

    const handleProductChange = (values: any[]) => {
        console.log("Product selected", values);
        setProducts(values);
        const prods = productList?.options?.map((p: any) => {
            return {
                price: p.value,
                id: p.label,
            };
        });
        console.log("Productz", prods);
        const total = values.reduce((acc, curr) => {
            const product = prods.find((p: any) => p.id === curr);
            console.log("Productz2", product);
            return product.price ? acc + product.price : acc;
        }, 0);
        setTotalAmount(total);
    }


    const CardTitle = () => {
        return (
            <div className="text-center px-6 pt-0 pb-1">
                <div className="mb-4">
                    <img src="/images/createorder.png" alt="logo" width={80} />
                </div>
            </div>
        );
    }

    const accountIdForLinks = accountIdFromParams ?? selectedAccountId ?? user?.account?.id;
    const orderShowPath =
        createdOrderId != null
            ? isAdmin
                ? `/orders/show/${createdOrderId}`
                : `/accounts/${accountIdForLinks}/orders/show/${createdOrderId}`
            : undefined;
    const ordersListPath = isAdmin
        ? "/orders"
        : `/accounts/${accountIdForLinks}/orders/list`;

    return (
        <>
            {/* One insertion point covers both the stepped and the legacy layout.
                The max-width matches the stepped column so it lines up. */}
            {!success && (
                <OpeningHoursNotice className="w-full xl:max-w-[70%] mx-auto mb-3" />
            )}
            {success ? (
                < Row
                    justify="center" align="middle" style={{ minHeight: '80vh' }}
                >
                    <Col className="sm:w-100 md:w-1/2">
                        <Card className="mt-16 text-center">
                            <Result
                                status="success"
                                className={success ? "block" : "hidden"}
                                title={_t('orders.titles.successMessage')}
                                subTitle={_t('orders.titles.successSubMessage')}
                                extra={
                                    createdOrderId != null && orderShowPath ? (
                                        <Space wrap>
                                            <Button type="primary" href={orderShowPath}>
                                                {_t("orders.titles.goToOrder")}
                                            </Button>
                                            <Button href={ordersListPath}>
                                                {_t("orders.titles.goToOrdersList")}
                                            </Button>
                                        </Space>
                                    ) : null
                                }
                            />
                        </Card>
                    </Col>
                </Row>
            ) : steppedMode ? (
                <OrderCreateStepped
                    formSchema={formSchema}
                    formId={formId}
                    user={user}
                    selectedAccountId={selectedAccountId}
                    setSelectedAccountId={setSelectedAccountId}
                    accountSelectProps={accountSelectProps}
                    isAdmin={isAdmin}
                    accountIdFromParams={accountIdFromParams}
                    isPrepaid={isPrepaid}
                    accountCredit={accountCredit}
                    open={open}
                    _t={_t}
                    mutate={mutate}
                    mutateMany={mutateMany}
                    apiUrl={apiUrl}
                    setSuccess={setSuccess}
                    onOrderCreated={setCreatedOrderId}
                    token={token}
                    onBuyModalOpen={handleBuyModalOpen}
                    productQueryResult={{
                        data: {
                            data: Array.isArray(productQueryResult.data?.data)
                                ? productQueryResult.data.data
                                : [],
                        },
                    }}
                    productList={productList}
                    onAccountCreditUpdated={setAccountCredit}
                />
            ) : (
                <>
                    <Flex vertical>
                        <Flex className="my-2">
                            {isAdmin ? (
                                <BackButton />
                            ) : (
                                <CustomerBackButton />
                            )}
                            <Title level={4}>{_t('orders.titles.create')}</Title>
                        </Flex>
                    </Flex>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={24} lg={16}>
                            <Form
                                form={form}
                                onFinish={onSubmit}
                                layout="vertical"
                            >
                                <FormRender schema={formSchema} />

                            </Form>

                        </Col>
                        <Col xs={24} md={24} lg={8}>
                            <Card className="shadow-lg">
                                <Typography.Title level={5} style={{ color: token.colorPrimaryText }}>
                                    <CodeOutlined className="mr-2" style={{ color: token.colorPrimary }} />
                                    {_t("orders.fields.reprogramming")}
                                </Typography.Title>

                                <Flex align="flex-start" vertical gap={16} className="mt-4">
                                    {isAdmin && !accountIdFromParams && (
                                        <Select
                                            placeholder={_t('orders.fields.account')}
                                            className="w-full"
                                            {...accountSelectProps}
                                            value={selectedAccountId}
                                            onChange={(value) => {
                                                setSelectedAccountId(value);
                                            }}
                                            suffixIcon={<IdcardOutlined />}
                                        />
                                    )}
                                    <Select
                                        placeholder={_t('orders.titles.selectProducts')}
                                        className="w-full"
                                        mode="multiple"
                                        allowClear
                                        onChange={handleProductChange}
                                        options={productOptions}
                                    />
                                    <Input.TextArea
                                        rows={3}
                                        placeholder={_t('orders.fields.comments')}
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                        className="w-full"
                                    />
                                </Flex>
                            </Card>

                            <Card className="shadow-lg mt-4">
                                <Title level={5}>
                                    <PaperClipOutlined className="mr-2" style={{ color: token.colorPrimary }} />
                                    {_t("orders.titles.attachments")}
                                </Title>

                                <div className="mt-4">
                                </div>

                                <Dragger {...fileUploadProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="text-base">{_t('orders.titles.uploadFile')}</p>

                                </Dragger>
                            </Card>


                            {isPrepaid && (
                                <Card className="shadow-lg mt-4">
                                    <Flex align="center" justify="space-between">
                                        <Flex align="center" gap={8}>
                                            <DollarOutlined className="text-xl text-orange-500" />
                                            <div className="text-base font-semibold">
                                                {_t('orders.fields.totalAmount')}
                                            </div>
                                        </Flex>
                                        <Typography.Title level={4} className="mt-2">
                                            {totalAmount} {_t("credits.credits")}
                                        </Typography.Title>
                                    </Flex>
                                    <Flex align="center">
                                        {totalAmount > accountCredit && (
                                            <Alert
                                                message={_t('orders.titles.insufficientCredit')}
                                                type="info"
                                                className="max-w-xl text-left"
                                                showIcon
                                            />
                                        )}
                                    </Flex>
                                </Card>
                            )}

                            {isPrepaid && (
                                <Card className="shadow-lg mt-4">
                                    <Flex align="flex-start" justify="space-between">
                                        <Flex align="center" gap={8}>
                                            <DollarOutlined className="text-xl text-green-500" />
                                            <div className="text-base font-semibold">
                                                {_t('credits.titles.balance')}
                                            </div>
                                        </Flex>
                                        <Flex vertical align="flex-end">
                                            <Typography.Title level={4} className="m-0">
                                                {accountCredit} {_t("credits.credits")}
                                            </Typography.Title>
                                            <Button
                                                type="primary"
                                                onClick={handleBuyModalOpen}
                                            >
                                                {_t('credits.titles.buy')}
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Card>
                            )}

                            <Flex justify="center" className="mt-8 px-2">
                                <Button type="primary" size="large" htmlType="submit" icon={<SendOutlined />} onClick={onSubmit}
                                    className="w-full shadow-md"
                                    loading={isSendingOrder}
                                    disabled={
                                        (isPrepaid && totalAmount > accountCredit) ||
                                        products.length === 0 ||
                                        uploadedFiles.length === 0 ||
                                        (isAdmin && !accountIdFromParams && !selectedAccountId && products.length === 0)
                                    }
                                >
                                    {_t("orders.titles.sendOrder")}
                                </Button>
                            </Flex>

                        </Col>
                    </Row>

                </>
            )}
            <CreditBuyModal
                isOpen={isBuyModalOpen}
                onCancel={handleBuyModalCancel}
                accountCredit={accountCredit}
            />
            <Flex
                justify="center"
                align="center"
                hidden
                className="py-4 border-t border-gray-200 dark:border-gray-700 mt-6"
            >
                <Switch
                    checked={steppedMode}
                    onChange={(checked) => {
                        setSteppedMode(checked);
                        try {
                            localStorage.setItem(ORDER_CREATE_STEPPED_KEY, String(checked));
                        } catch {
                            // ignore
                        }
                    }}
                />
                <span className="ml-2">{_t("orders.stepped.preferenceLabel")}</span>
            </Flex>
        </>
    );
}